import React, { useEffect, useState, useCallback } from 'react';
import { toast } from 'react-toastify';
import {
  EllipsisVerticalIcon,
  PencilIcon,
  TrashIcon,
  EnvelopeIcon,
  ShieldCheckIcon,
  PaperAirplaneIcon,
  ClockIcon,
  XCircleIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';
import {
  Card,
  Button,
  Table,
  Badge,
  Avatar,
  Dropdown,
  ConfirmDialog,
  EmptyState,
  Modal,
  Input,
  Select,
} from '../components/ui';
import { useUserStore, useAuthStore } from '../stores';
import type { User, UserRole, UserStatus } from '../types';
import { formatDate, capitalize } from '../utils/helpers';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { userSchema, type UserFormData } from '../utils/validation';
import { invitationService, type Invitation } from '../services/invitation.service';

const getRoleVariant = (role: UserRole) => {
  const variants: Record<UserRole, 'primary' | 'accent' | 'warning' | 'secondary'> = {
    admin: 'primary',
    manager: 'accent',
    customer_service: 'warning',
    viewer: 'secondary',
  };
  return variants[role] || 'secondary';
};

const getStatusVariant = (status: UserStatus) => {
  const variants: Record<UserStatus, 'success' | 'secondary' | 'warning'> = {
    active: 'success',
    inactive: 'secondary',
    pending: 'warning',
  };
  return variants[status];
};

export const Users: React.FC = () => {
  const { users, roles, isLoading, fetchUsers, fetchRoles, updateUser, updateUserRole, deleteUser } = useUserStore();
  const { hasPermission, user: currentUser } = useAuthStore();

  // Permission checks
  const canCreateUsers = hasPermission('users:create');
  const canUpdateUsers = hasPermission('users:update');
  const canDeleteUsers = hasPermission('users:delete');
  const canAssignRoles = hasPermission('users:assign_role');

  const [modalOpen, setModalOpen] = useState(false);
  const [roleModalOpen, setRoleModalOpen] = useState(false);
  const [inviteModalOpen, setInviteModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [userToChangeRole, setUserToChangeRole] = useState<User | null>(null);
  const [selectedRoleId, setSelectedRoleId] = useState<string>('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState<User | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isChangingRole, setIsChangingRole] = useState(false);

  // Invitation state
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRoleId, setInviteRoleId] = useState('');
  const [isSendingInvite, setIsSendingInvite] = useState(false);
  const [inviteError, setInviteError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<UserFormData>({
    resolver: zodResolver(userSchema),
  });

  const fetchInvitations = useCallback(async () => {
    try {
      const data = await invitationService.getInvitations();
      setInvitations(data);
    } catch (error) {
      console.error('Failed to fetch invitations:', error);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
    fetchRoles();
    fetchInvitations();
  }, [fetchUsers, fetchRoles, fetchInvitations]);

  const handleSendInvite = async () => {
    if (!inviteEmail.trim()) {
      setInviteError('Email is required');
      return;
    }

    setIsSendingInvite(true);
    setInviteError(null);

    try {
      await invitationService.createInvitation({
        email: inviteEmail.trim().toLowerCase(),
        roleId: inviteRoleId || undefined,
      });
      toast.success('Invitation sent successfully!');
      setInviteModalOpen(false);
      setInviteEmail('');
      setInviteRoleId('');
      fetchInvitations();
    } catch (error: any) {
      setInviteError(error.response?.data?.error || 'Failed to send invitation');
    } finally {
      setIsSendingInvite(false);
    }
  };

  const handleResendInvite = async (invitationId: string) => {
    try {
      await invitationService.resendInvitation(invitationId);
      toast.success('Invitation resent!');
      fetchInvitations();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to resend invitation');
    }
  };

  const handleRevokeInvite = async (invitationId: string) => {
    try {
      await invitationService.revokeInvitation(invitationId);
      toast.success('Invitation revoked');
      fetchInvitations();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to revoke invitation');
    }
  };

  const handleOpenModal = (user: User) => {
    setEditingUser(user);
    reset({
      email: user.email,
      firstName: user.firstName,
      lastName: user.lastName,
      phone: user.phone || '',
      role: user.role,
    });
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setEditingUser(null);
    reset();
  };

  const onSubmit = async (data: UserFormData) => {
    if (!editingUser) return;
    setIsSubmitting(true);
    try {
      await updateUser(editingUser.id, {
        firstName: data.firstName,
        lastName: data.lastName,
        phone: data.phone,
      });
      toast.success('User updated successfully');
      handleCloseModal();
    } catch (error) {
      toast.error('Failed to update user');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteClick = (user: User) => {
    setUserToDelete(user);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!userToDelete) return;
    setIsDeleting(true);
    try {
      await deleteUser(userToDelete.id);
      toast.success('User deleted successfully');
      setDeleteDialogOpen(false);
      setUserToDelete(null);
    } catch (error) {
      toast.error('Failed to delete user');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleOpenRoleModal = (user: User) => {
    setUserToChangeRole(user);
    // Find the role ID for the user's current role
    const currentRole = roles.find((r) => r.name === user.role);
    setSelectedRoleId(currentRole?.id || '');
    setRoleModalOpen(true);
  };

  const handleCloseRoleModal = () => {
    setRoleModalOpen(false);
    setUserToChangeRole(null);
    setSelectedRoleId('');
  };

  const handleRoleChange = async () => {
    if (!userToChangeRole || !selectedRoleId) return;
    setIsChangingRole(true);
    try {
      await updateUserRole(userToChangeRole.id, selectedRoleId);
      toast.success('User role updated successfully');
      handleCloseRoleModal();
    } catch (error) {
      toast.error('Failed to update user role');
    } finally {
      setIsChangingRole(false);
    }
  };

  const columns = [
    {
      key: 'name',
      header: 'User',
      render: (user: User) => (
        <div className="flex items-center space-x-3">
          <Avatar
            firstName={user.firstName}
            lastName={user.lastName}
            src={user.avatar}
            size="md"
          />
          <div>
            <p className="font-medium text-secondary-900 dark:text-secondary-100">
              {user.firstName} {user.lastName}
            </p>
            <p className="text-sm text-secondary-500 dark:text-secondary-400">{user.email}</p>
          </div>
        </div>
      ),
    },
    {
      key: 'role',
      header: 'Role',
      render: (user: User) => (
        <Badge variant={getRoleVariant(user.role)}>
          {user.roleDisplayName}
        </Badge>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (user: User) => (
        <Badge variant={getStatusVariant(user.status)} dot>
          {capitalize(user.status)}
        </Badge>
      ),
    },
    {
      key: 'createdAt',
      header: 'Joined',
      render: (user: User) => (
        <span className="text-secondary-600 dark:text-secondary-400">{formatDate(user.createdAt)}</span>
      ),
    },
    {
      key: 'actions',
      header: '',
      className: 'w-12',
      render: (user: User) => {
        const isCurrentUser = currentUser?.id === user.id;
        const dropdownItems = [];

        if (canUpdateUsers) {
          dropdownItems.push({
            label: 'Edit',
            icon: <PencilIcon className="h-4 w-4" />,
            onClick: () => handleOpenModal(user),
          });
        }

        if (canAssignRoles && !isCurrentUser) {
          dropdownItems.push({
            label: 'Change Role',
            icon: <ShieldCheckIcon className="h-4 w-4" />,
            onClick: () => handleOpenRoleModal(user),
          });
        }

        dropdownItems.push({
          label: 'Resend Invite',
          icon: <EnvelopeIcon className="h-4 w-4" />,
          onClick: () => toast.info('Invite resent'),
          disabled: user.status !== 'pending',
        });

        if (canDeleteUsers && !isCurrentUser) {
          dropdownItems.push({
            label: 'Delete',
            icon: <TrashIcon className="h-4 w-4" />,
            onClick: () => handleDeleteClick(user),
            danger: true,
          });
        }

        if (dropdownItems.length === 0) {
          return null;
        }

        return (
          <Dropdown
            trigger={
              <button className="p-1 rounded-lg hover:bg-secondary-100 dark:hover:bg-secondary-700 transition-colors">
                <EllipsisVerticalIcon className="h-5 w-5 text-secondary-500 dark:text-secondary-400" />
              </button>
            }
            items={dropdownItems}
          />
        );
      },
    },
  ];

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-secondary-900 dark:text-secondary-50">Users</h1>
          <p className="text-secondary-500 dark:text-secondary-400">Manage team members and permissions</p>
        </div>
        {canCreateUsers && (
          <Button
            leftIcon={<PaperAirplaneIcon className="h-5 w-5" />}
            onClick={() => setInviteModalOpen(true)}
          >
            Send Invite
          </Button>
        )}
      </div>

      {/* Pending Invitations Section */}
      {invitations.filter(i => i.status === 'pending').length > 0 && (
        <Card>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-secondary-900 dark:text-secondary-50">
              Pending Invitations
            </h2>
            <Badge variant="warning">{invitations.filter(i => i.status === 'pending').length}</Badge>
          </div>
          <div className="space-y-3">
            {invitations.filter(i => i.status === 'pending').map((invitation) => (
              <div 
                key={invitation.id} 
                className="flex items-center justify-between p-3 bg-secondary-50 dark:bg-secondary-800 rounded-lg"
              >
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 rounded-full bg-warning-100 dark:bg-warning-900/30 flex items-center justify-center">
                    <ClockIcon className="w-5 h-5 text-warning-600 dark:text-warning-400" />
                  </div>
                  <div>
                    <p className="font-medium text-secondary-900 dark:text-secondary-100">{invitation.email}</p>
                    <p className="text-sm text-secondary-500 dark:text-secondary-400">
                      {invitation.roleName || 'No role assigned'} · Expires {formatDate(invitation.expiresAt)}
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    leftIcon={<ArrowPathIcon className="h-4 w-4" />}
                    onClick={() => handleResendInvite(invitation.id)}
                  >
                    Resend
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    leftIcon={<XCircleIcon className="h-4 w-4" />}
                    onClick={() => handleRevokeInvite(invitation.id)}
                  >
                    Revoke
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Table */}
      <Card padding="none">
        {users.length === 0 && !isLoading ? (
          <EmptyState
            title="No users found"
            description="Invite team members to join your organization"
            action={
              canCreateUsers ? (
                <Button
                  leftIcon={<PaperAirplaneIcon className="h-5 w-5" />}
                  onClick={() => setInviteModalOpen(true)}
                >
                  Send Invite
                </Button>
              ) : undefined
            }
          />
        ) : (
          <Table columns={columns} data={users} keyExtractor={(user) => user.id} isLoading={isLoading} />
        )}
      </Card>

      {/* Edit User Modal */}
      <Modal
        isOpen={modalOpen}
        onClose={handleCloseModal}
        title="Edit User"
        size="md"
      >
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <Input
              label="First Name"
              placeholder="John"
              error={errors.firstName?.message}
              {...register('firstName')}
            />
            <Input
              label="Last Name"
              placeholder="Doe"
              error={errors.lastName?.message}
              {...register('lastName')}
            />
          </div>

          <Input
            label="Email"
            type="email"
            placeholder="john@company.com"
            disabled
            error={errors.email?.message}
            {...register('email')}
          />

          <Input
            label="Phone (Optional)"
            placeholder="(555) 123-4567"
            error={errors.phone?.message}
            {...register('phone')}
          />

          <div className="flex justify-end space-x-3 pt-4">
            <Button type="button" variant="secondary" onClick={handleCloseModal}>
              Cancel
            </Button>
            <Button type="submit" isLoading={isSubmitting}>
              Update User
            </Button>
          </div>
        </form>
      </Modal>

      {/* Delete Confirmation */}
      <ConfirmDialog
        isOpen={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDeleteConfirm}
        title="Delete User"
        message={`Are you sure you want to delete ${userToDelete?.firstName} ${userToDelete?.lastName}? This action cannot be undone.`}
        confirmLabel="Delete"
        isLoading={isDeleting}
      />

      {/* Change Role Modal */}
      <Modal
        isOpen={roleModalOpen}
        onClose={handleCloseRoleModal}
        title="Change User Role"
        size="sm"
      >
        <div className="space-y-4">
          <p className="text-secondary-600 dark:text-secondary-300">
            Change the role for <strong>{userToChangeRole?.firstName} {userToChangeRole?.lastName}</strong>
          </p>

          <Select
            label="Role"
            value={selectedRoleId}
            onChange={(e) => setSelectedRoleId(e.target.value)}
            options={roles.map((role) => ({
              value: role.id,
              label: role.displayName,
            }))}
          />

          <div className="flex justify-end space-x-3 pt-4">
            <Button type="button" variant="secondary" onClick={handleCloseRoleModal}>
              Cancel
            </Button>
            <Button
              onClick={handleRoleChange}
              isLoading={isChangingRole}
              disabled={!selectedRoleId}
            >
              Update Role
            </Button>
          </div>
        </div>
      </Modal>

      {/* Send Invite Modal */}
      <Modal
        isOpen={inviteModalOpen}
        onClose={() => {
          setInviteModalOpen(false);
          setInviteEmail('');
          setInviteRoleId('');
          setInviteError(null);
        }}
        title="Send Invitation"
        size="sm"
      >
        <div className="space-y-4">
          <p className="text-secondary-600 dark:text-secondary-300">
            Invite a new team member to join your organization. They'll receive an email with a link to create their account.
          </p>

          <Input
            label="Email address"
            type="email"
            placeholder="colleague@company.com"
            value={inviteEmail}
            onChange={(e) => setInviteEmail(e.target.value)}
            leftIcon={<EnvelopeIcon className="h-5 w-5" />}
          />

          <Select
            label="Role (optional)"
            value={inviteRoleId}
            onChange={(e) => setInviteRoleId(e.target.value)}
            options={[
              { value: '', label: 'Select a role...' },
              ...roles.map((role) => ({
                value: role.id,
                label: role.displayName,
              })),
            ]}
          />

          {inviteError && (
            <div className="p-3 rounded-lg bg-danger-50 dark:bg-danger-900/30 text-danger-700 dark:text-danger-300 text-sm">
              {inviteError}
            </div>
          )}

          <div className="flex justify-end space-x-3 pt-4">
            <Button 
              type="button" 
              variant="secondary" 
              onClick={() => {
                setInviteModalOpen(false);
                setInviteEmail('');
                setInviteRoleId('');
                setInviteError(null);
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSendInvite}
              isLoading={isSendingInvite}
              leftIcon={<PaperAirplaneIcon className="h-4 w-4" />}
            >
              Send Invite
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default Users;



